import {
  FieldFunctionOptions,
  FieldPolicy,
  Reference,
  TypePolicies,
} from "@apollo/client";
import { generateUuid } from "../../utils/generate-uuid";
import { cloneList } from "../../utils/utils";
import companyPicVar from "../variables/companyPicVar";
import isLoggedInVar from "../variables/isLoggedInVar";
import profilePicVar from "../variables/profilePicVar";
import customCursorPagination from "./customCursorPagination";
import { tasks } from "./tasks";
import { VITE_REACT_APP_PUBLIC_S3 } from "../../constants/variables";

type FieldFunction = {
    // eslint-disable-next-line @typescript-eslint/no-wrapper-object-types
    read(existing: any, { readField }: FieldFunctionOptions<Record<string, any>, Record<string, any>>): string | number | void | Readonly<Object>;
}

const userNameFieldFn: FieldFunction = {
  read(existing, { readField }) {
    const firstName = readField("firstName");
    const lastName = readField("lastName");
    const email = readField("email");
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    return email;
  }
};

const userLogoFieldFn: FieldFunction = {
  read(_, { readField }) {
    let time = "";
    const updatedAt: string = readField("updatedAt");
    if (updatedAt) {
      time = `?timestamp=${new Date(updatedAt).getTime()}`;
    }
    const id = readField("id");
    if (!id) {
      return undefined;
    }
    return `${VITE_REACT_APP_PUBLIC_S3}/users/${id}${time}`;
  }
};

const itemsKeyArgs: FieldPolicy["keyArgs"] = (args) => {
  if (args?.positionDay != null) {
    return ["taskId", "scope", "positionDay", "positionTimezone"];
  }
  if (args?.itemIds == null) {
    return ["taskId"];
  }

  return ["taskId", "scope", "itemIds"];
};

const typePolicies: TypePolicies = {
  // Matching files belong to the query predicate, not to a global item snapshot.
  ItemSearchResult: { keyFields: false },
  Query: {
    fields: {
      accountingElements: customCursorPagination(["id"]),
      search: tasks,
      groups: customCursorPagination(false, false, (args) => ({
        cursor: args?.cursor,
        previous: null,
      })),
      items: customCursorPagination(itemsKeyArgs, true),
      searchTasks: customCursorPagination(["pinnedFirst"], false, (args) => ({
        cursor: args?.pagination.cursor,
        previous: null,
      })),
      searchItems: customCursorPagination(["filters", "sortField", "sortDir", "scope", "positionDay", "positionTimezone"], true, (args) => ({
        cursor: args?.pagination.cursor,
        previous: args?.pagination.previousCursor,
      })),
      searchAttachments: customCursorPagination(["filters", "category", "sortField", "sortDir", "scope", "positionDay", "positionTimezone"], true, (args) => ({
        cursor: args?.pagination.cursor,
        previous: args?.pagination.previousCursor,
      })),
      tasksSummary: {
        keyArgs: [],
        merge(_, incoming) {
          return incoming;
        },
      },
      sectionSummaryDetails: {
        keyArgs: ["sectionId"],
        merge(_, incoming) {
          return incoming;
        },
      },
      getProfilePic: {
        read() {
          return profilePicVar();
        },
      },
      getCompanyPic: {
        read() {
          return companyPicVar();
        },
      },
      isLoggedIn: {
        read() {
          return isLoggedInVar();
        },
      },
      projects: {
        merge(existing, incoming, { readField }) {
          if (!incoming) {
            return existing;
          }
          return cloneList(incoming).sort((a: Reference, b: Reference) => {
            const aField: string = readField("name", a);
            const bField: string = readField("name", b);
            if (aField.toLowerCase() < bField.toLowerCase()) {
              return -1;
            }
            return 1;
          });
        },
      },
      templates: {
        keyArgs: (_, { typename, variables }) =>
          `${typename}:${variables?.filters?.taskId}:template`,
      },
    },
  },
  Templates: {
    // keyFields: (_, { typename }) => `${typename}:template`,
    fields: {
      taskTypes: {
        merge: (_, incoming) => incoming,
      },
      checklists: {
        merge: (_, incoming) => incoming,
      },
      sections: {
        merge: (_, incoming) => incoming,
      },
    },
  },
  ProductData: {
    keyFields: ["id", "period"],
  },
  Chat: {
    keyFields: ["chatId"],
  },
  User: {
    fields: {
      name: userNameFieldFn,
      logo: userLogoFieldFn,
      // updatedAt: {
      //   merge() {
      //     return new Date().toISOString();
      //   },
      // },
    },
  },
  TaskUserInfo: {
    fields: {
      name: userNameFieldFn,
      logo: userLogoFieldFn,
    }
  },
  TaskUser: {
    fields: {
      name: userNameFieldFn,
      logo: userLogoFieldFn,
    },
    keyFields: false,
  },
  TaskGroup: {
    keyFields: false,
  },
  TaskListMemberProfile: {
    keyFields: false,
  },
  TaskListMemberSet: {
    keyFields: false,
  },
  TaskListTaskGroup: {
    keyFields: false,
  },
  TaskListTaskUser: {
    keyFields: false,
  },
  TaskSectionSummary: {
    keyFields: false,
  },
  // Per-day task hours in the presence summary. It carries a task `id`, but the
  // same task appears across multiple days with a different `totalQuantity`.
  // Without this, Apollo would normalize by id and collapse every day's value
  // into a single shared entry (e.g. a day showing another day's hours).
  AccountingSectionSummaryTask: {
    keyFields: false,
  },
  ContactInfo: {
    fields: {
      name: {
        read(existing, { readField }) {
          const email = readField("email");

          let name = existing || "";
          if (!name) {
            return email;
          }

          name = name
            .replace(/undefined/g, "")
            .replace(/null/g, "")
            .trim();
          if (name) {
            return name;
          }
          return email;
        },
      },
    },
  },
  UserInfo: {
    fields: {
      name: {
        read(existing, { readField }) {
          const email = readField("email");
          return email;
        },
      },
    },
  },
  AccountingItemSection: {
    keyFields: ["uniqueKey"],
  },
  // Search returns only transferId; keep the embedded chat metadata per item.
  WarehouseMovement: {
    merge: true,
  },
  AccountingItemSectionData: {
    fields: {
      uniqueKey: {
        // Keep the fallback stable for this cached row so fetchMore can settle.
        read(existing, { storage }) {
          return existing || (storage.uniqueKey ??= generateUuid());
        },
      },
    },
  },
  AccountingSection: {
    keyFields: ["key"],
  },
  AccountingInstanceSection: {
    keyFields: ["key"],
  },
  File: {
    keyFields: ["signedUrl"],
  },
  CustomFieldTemplateSource: {
    keyFields: ["id", "column"],
  },
  CheckListFieldTemplateSource: {
    keyFields: ["id", "column"],
  },
  License: {
    keyFields: false
  },
  ProductPrice: {
    keyFields: false
  },
  GroupUser: {
    keyFields: false
  },

  Item: {
    fields: {
      // Each response is a complete reaction snapshot, including removals.
      reactions: { merge: false },
    },
  },
};

export default typePolicies;
